"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PLOT_HEIGHT,
  PLOT_WIDTH,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber } from "@/components/tools/tool-ui";
import { downloadPng } from "@/lib/tool-kit/download";
import {
  equalizeAspect,
  padRange,
  type Viewport,
} from "@/lib/tool-kit/math/plot";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

export interface PlotArrow {
  x: number;
  y: number;
  color: string;
  label: string;
  /** Draw from the origin (default) or from this point. */
  from?: { x: number; y: number };
  dashed?: boolean;
}

export interface PlotSegment {
  from: { x: number; y: number };
  to: { x: number; y: number };
  color: string;
}

const HEAD = 10;

/** A line with an arrowhead at its tip. */
function Arrow({
  x1,
  y1,
  x2,
  y2,
  color,
  dashed,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  dashed?: boolean;
}) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const left = [
    x2 - HEAD * Math.cos(angle - Math.PI / 7),
    y2 - HEAD * Math.sin(angle - Math.PI / 7),
  ];
  const right = [
    x2 - HEAD * Math.cos(angle + Math.PI / 7),
    y2 - HEAD * Math.sin(angle + Math.PI / 7),
  ];
  return (
    <g>
      <line
        stroke={color}
        strokeDasharray={dashed ? "6 4" : undefined}
        strokeWidth={2.5}
        x1={x1}
        x2={x2}
        y1={y1}
        y2={y2}
      />
      <polygon
        fill={color}
        points={`${x2},${y2} ${left[0]},${left[1]} ${right[0]},${right[1]}`}
      />
    </g>
  );
}

/** The window that holds every point with padding, units equal on both axes. */
function vectorWindow(points: { x: number; y: number }[]): Viewport {
  const xs = [0, ...points.map((p) => p.x)];
  const ys = [0, ...points.map((p) => p.y)];
  const span = Math.max(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
    1
  );
  const [xMin, xMax] = padRange(Math.min(...xs), Math.max(...xs), 0.2, span);
  const [yMin, yMax] = padRange(Math.min(...ys), Math.max(...ys), 0.2, span);
  return equalizeAspect({ xMin, xMax, yMin, yMax }, PLOT_WIDTH, PLOT_HEIGHT);
}

/** 2D vectors drawn from the origin to scale, with optional guide segments. */
export function VectorPlot({
  arrows,
  segments = [],
  title,
  downloadLabel,
  fileName,
}: {
  arrows: PlotArrow[];
  segments?: PlotSegment[];
  title: string;
  downloadLabel: string;
  fileName: string;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const viewport = vectorWindow([
    ...arrows.flatMap((a) => [{ x: a.x, y: a.y }, ...(a.from ? [a.from] : [])]),
    ...segments.flatMap((s) => [s.from, s.to]),
  ]);
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={title}
        viewport={viewport}
      >
        {(scale) => (
          <>
            {segments.map((s) => (
              <line
                key={`${s.from.x},${s.from.y}-${s.to.x},${s.to.y}`}
                stroke={s.color}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                x1={scale.x(s.from.x)}
                x2={scale.x(s.to.x)}
                y1={scale.y(s.from.y)}
                y2={scale.y(s.to.y)}
              />
            ))}
            {arrows.map((a) => {
              const fx = a.from?.x ?? 0;
              const fy = a.from?.y ?? 0;
              return (
                <g key={a.label}>
                  <Arrow
                    color={a.color}
                    dashed={a.dashed}
                    x1={scale.x(fx)}
                    x2={scale.x(a.x)}
                    y1={scale.y(fy)}
                    y2={scale.y(a.y)}
                  />
                  <text
                    fill={CHART_INK.text}
                    fontSize={13}
                    fontWeight={600}
                    textAnchor={a.x >= fx ? "start" : "end"}
                    x={scale.x(a.x) + (a.x >= fx ? 8 : -8)}
                    y={scale.y(a.y) + (a.y >= fy ? -8 : 16)}
                  >
                    {a.label}
                  </text>
                </g>
              );
            })}
          </>
        )}
      </CoordinatePlane>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng(fileName, ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}

export const PLOT_PALETTE = CHART_COLORS;
